import { useAvailableAuction } from 'api/auctions'
import classNames from 'classnames'
import { Button } from 'components/common/Button'
import { useFeatureFlags } from 'hooks'
import { useCountdown } from 'hooks/useCountdown'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { getAuctionUrl } from 'utils/auctions'

const variants = {
  sticky: 'sticky',
  absolute: 'absolute',
  static: 'static',
} as const

interface Props {
  variant?: keyof typeof variants
}

export function AvailableAuctionBanner({ variant = 'static' }: Props) {
  const { t } = useTranslation('common')
  const { availableAuction } = useAvailableAuction()
  const { featureFlags } = useFeatureFlags()
  const targetDate = availableAuction?.status?.pending
    ? availableAuction?.status.startDate
    : availableAuction?.status.endDate
  const countdown = useCountdown(targetDate?.toDate())

  if (!availableAuction || !featureFlags?.['enable-auction']) return null

  return (
    <header
      className={classNames(
        ' top-16 z-40 flex w-full items-center justify-center gap-x-2 gap-y-2 whitespace-nowrap bg-primary-default px-12 py-2.5 md:gap-x-4 md:py-2 lg:top-20  [@media(max-width:425px)]:flex-col',
        variants[variant]
      )}
    >
      <div className='text-center text-sm text-cool-50 md:text-base'>
        <p className='md:hidden'>
          <Trans
            i18nKey={
              availableAuction.status.pending
                ? 'common:available_auction_banner.upcoming_title'
                : 'common:available_auction_banner.ongoing_title'
            }
            components={{ b: <b /> }}
            values={{
              startDate: availableAuction.status.startDate.format('Do MMM'),
              artistName: availableAuction.artistName,
            }}
          />
        </p>
        <p className='hidden md:block'>
          <Trans
            i18nKey={
              availableAuction.status.pending
                ? 'common:available_auction_banner.upcoming_extended_title'
                : 'common:available_auction_banner.ongoing_extended_title'
            }
            components={{ b: <b /> }}
            values={{
              countdown: `${countdown.days}d ${countdown.hrs}h ${countdown.min}min`,
              artistName: availableAuction.artistName,
            }}
          />
        </p>
      </div>
      <Button
        style='solid-white'
        pill
        size='sm'
        type='link'
        href={getAuctionUrl(
          availableAuction.artistName || '',
          availableAuction.artworkName || '',
          availableAuction.id
        )}
      >
        <span className='md:hidden'>
          {t('available_auction_banner.extended_action')}
        </span>
        <span className='hidden md:block'>
          {t('available_auction_banner.action')}
        </span>
      </Button>
    </header>
  )
}
